import React, { useState } from "react";
import * as R from "ramda";
import globalMU from "../../../globalMU";
import { Documento, RespostaCredito } from "app/types/cliente/RespostaCredito";
import clsx from "clsx";
import { classeParaLabelsFormularioComFundoEscuroSimplificada } from "app/estilos/classeParaLabelsFormularioComFundoEscuro";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const estiloTabela = require('../../estilos/tabelas.module.scss').default
const estilosFormulario = require('../../estilos/formulario.module.scss').default
const estilosBotoes = require('../../estilos/botoes.module.scss').default

type ColunaCredito = {
  texto: string;
  coluna: string;
  estilo: string;
  permitido: boolean;
};

export type CreditosCreditosClienteProps = {
  permitirImpressaoCredito: boolean;
  permitirExibirCamposFormaPagamento: boolean;
  usuarioPermitidoImpressao: boolean;
  onImprimir: (documento: Documento[]) => void;
  cpfcnpj: string;
  nome: string;
  corSelecionado?: string;
  textoSelecionado?: string;
  dadosCredito: RespostaCredito;
  onAlterarOrdenacaoDados: (coluna: string, decrescente: boolean) => void;
  colunaOrdenacao: string
  ordemOrdenacaoDecrescente: boolean
  usaFonteDeReceita?: boolean // 487678/449093
};

export const CreditosCliente = React.memo((props: CreditosCreditosClienteProps) => {
  const {
    permitirImpressaoCredito,
    permitirExibirCamposFormaPagamento,
    usuarioPermitidoImpressao,
    onImprimir,
    cpfcnpj,
    nome,
    corSelecionado,
    textoSelecionado,
    dadosCredito,
    onAlterarOrdenacaoDados,
    colunaOrdenacao,
    ordemOrdenacaoDecrescente,
    usaFonteDeReceita,
  } = props;

  const [documentosSelecionados, setDocumentosSelecionados] = useState<Documento[]>([]);

  const toggleDocumentoselecionado = React.useCallback((credito: Documento) => {
    setDocumentosSelecionados((selecionados) => selecionados.includes(credito)
      ? R.filter((e) => e != credito, selecionados)
      : selecionados.concat(credito));
  }, []);

  const alterarOrdenacao = React.useCallback((coluna: string) => {
    onAlterarOrdenacaoDados(coluna, colunaOrdenacao == coluna ? ordemOrdenacaoDecrescente ? false : true : false)
  }, [colunaOrdenacao, ordemOrdenacaoDecrescente, onAlterarOrdenacaoDados]);

  const dadosCliente = React.useMemo(() => (
    <section id="CreditosCliente_DadosCliente"
      className={clsx(estilosFormulario.formularioSimplificado, classeParaLabelsFormularioComFundoEscuroSimplificada)}>
      <label id="CreditosCliente_DadosCliente_LabelCpfCnpj">CPF/CNPJ</label>
      <span id="CreditosCliente_DadosCliente_CpfCnpj">{cpfcnpj}</span>
      <label id="CreditosCliente_DadosCliente_LabelNome">Nome</label>
      <span id="CreditosCliente_DadosCliente_Nome">{nome}</span>
      {<section id="CreditosCliente_DadosCliente_Espacamento" style={{ marginBottom: "20px" }} />}
    </section>
  ), [cpfcnpj, nome]);

  const botaoImprmir = React.useMemo(() => (
    (permitirImpressaoCredito && usuarioPermitidoImpressao && (
      <button className={`no-global-button ${estilosBotoes.botaoPrimario}`}
        onClick={() => onImprimir(documentosSelecionados)}
        title="Veja Parâmetros:&#0013;-Trabalha com controle de impressão de nota de crédito?&#0013;-Número de dias para bloqueio da impressão da nota de crédito&#0013;-Restringir a utilização do crédito de cliente somente na filial de origem do crédito?&#0013;-Validar desconto máximo da forma de pagamento do crédito do cliente no orçamento?"
        disabled={documentosSelecionados.length == 0}
        id="CreditosCliente_BotaoImprimirCredito"
      >Imprimir Crédito
      </button>
    ))
  ), [permitirImpressaoCredito, usuarioPermitidoImpressao, onImprimir, documentosSelecionados]);

  const colunas = React.useMemo<ColunaCredito[]>(() => [
    { texto: "Número", coluna: "Numero", estilo: estiloTabela.cabecalhoTexto, permitido: true },
    { texto: "Vencimento", coluna: "DataVencimento", estilo: estiloTabela.cabecalhoTexto, permitido: true },
    { texto: "Crédito Total", coluna: "CreditoTotal", estilo: estiloTabela.cabecalhoNumerico, permitido: true },
    { texto: "Crédito Utilizado", coluna: "CreditoUtilizado", estilo: estiloTabela.cabecalhoNumerico, permitido: true },
    { texto: "Crédito Disponível", coluna: "CreditoDisponivel", estilo: estiloTabela.cabecalhoNumerico, permitido: true },
    { texto: "Forma de Pagamento", coluna: "FormaDePagar", estilo: estiloTabela.cabecalhoTexto, permitido: permitirExibirCamposFormaPagamento },
    { texto: "% Desconto Máximo", coluna: "Formula", estilo: estiloTabela.cabecalhoNumerico, permitido: permitirExibirCamposFormaPagamento },
    { texto: "Fonte de Receita", coluna: "FonteDeReceita", estilo: estiloTabela.cabecalhoTexto, permitido: usaFonteDeReceita == true }, // 487678/449093
    { texto: "Nº Vias", coluna: "NumeroVias", estilo: estiloTabela.cabecalhoNumerico, permitido: true },
  ], [permitirExibirCamposFormaPagamento, usaFonteDeReceita]);

  const cabecalhoTabelaCreditos = React.useMemo(() => (
    <thead id="CreditosCliente_Cabecalho">
      <tr id="CreditosCliente_Cabecalho_Linha">
        {colunas.filter((x) => x.permitido).map((x, index) => (
          <th className={x.estilo}
            id={`CreditosCliente_Cabecalho_${x.coluna}_${index}`}
            key={`CreditosCliente_Cabecalho_${x.coluna}_${index}`}
          >
            <div className="_flex _justify-between"
              id={`CreditosCliente_Cabecalho_Conteudo_${x.coluna}_${index}`}
            >
              {x.texto}
              <div id={`CreditosCliente_Cabecalho_Ordenacao_${x.coluna}_${index}`}>
                <FontAwesomeIcon
                  color={colunaOrdenacao == x.coluna ? "#008000" : undefined}
                  icon={colunaOrdenacao == x.coluna
                    ? ordemOrdenacaoDecrescente
                      ? faChevronUp
                      : faChevronDown
                    : faChevronDown}
                  cursor={"pointer"}
                  onClick={() => alterarOrdenacao(x.coluna)}
                />
              </div>
            </div>
          </th>
        ))}
      </tr>
    </thead>
  ), [colunas, colunaOrdenacao, ordemOrdenacaoDecrescente, alterarOrdenacao]);

  const corpoTabelaCreditos = React.useMemo(() => (
    <tbody
      id="CreditosCliente_CorpoTabelaCreditos"
    >
      {dadosCredito?.Documentos?.map((value, index) => {
        const selecionado = documentosSelecionados.includes(value);

        return (
          <tr
            key={`CreditosCliente_Linha_${index}`}
            id={`CreditosCliente_Linha_${index}`}
            className={`_table-row ${selecionado
              ? estiloTabela.linhaSelecionada
              : estiloTabela.linha
              }`}
            style={
              selecionado
                ? {
                  backgroundColor: corSelecionado,
                  color: textoSelecionado,
                }
                : {}
            }
            onClick={() => { toggleDocumentoselecionado(value); }}
          >
            <td className={estiloTabela.dadoTexto}
              id={`CreditosCliente_Numero_${index}`}
            >{value.Numero}</td>
            <td className={estiloTabela.dadoTexto}
              id={`CreditosCliente_DataVencimento_${index}`}
            >{globalMU.formatDate(value.DataVencimento)}</td>
            <td id={`CreditosCliente_CreditoTotal_${index}`}
            >{globalMU.formatarValorNumerico(value.CreditoTotal, 2, true)}</td>
            <td id={`CreditosCliente_CreditoUtilizado_${index}`}
            >{value.CreditoUtilizado && value.CreditoUtilizado > 0 ?
              globalMU.formatarValorNumerico(value.CreditoUtilizado, 2, true) : null}</td>
            <td id={`CreditosCliente_CreditoDisponivel_${index}`}
            >{globalMU.formatarValorNumerico(value.CreditoDisponivel, 2, true)}</td>
            {permitirExibirCamposFormaPagamento && (
              <>
                <td className={estiloTabela.dadoTexto}
                  id={`CreditosCliente_FormaDePagar_${index}`}
                >{value.FormaDePagar}</td>
                <td id={`CreditosCliente_Formula_${index}`}
                >{value.Formula}</td>
              </>
            )}
            {usaFonteDeReceita && (
              <td className={estiloTabela.dadoTexto}
                id={`CreditosCliente_FonteDeReceita_${index}`}
              >{value.FonteDeReceita}</td>
            )}
            <td id={`CreditosCliente_NumeroVias_${index}`}
            >{value.NumeroVias}</td>

          </tr>
        );
      })}
    </tbody>
  ), [dadosCredito, documentosSelecionados, corSelecionado, textoSelecionado, permitirExibirCamposFormaPagamento, usaFonteDeReceita, toggleDocumentoselecionado]);

  const rodapeTabelaCreditos = React.useMemo(() => (
    <tfoot style={{ height: "25px" }}
      id="CreditosCliente_RodapeTabelaCreditos"
    >
      <tr
        id={`CreditosCliente_Rodape_linha`}
      >
        <th colSpan={4} className={estiloTabela.cabecalhoNumerico}
          id="CreditosCliente_Rodape_LabelTotalDisponivel"
        >Total de Crédito Disponível</th>
        <th className={estiloTabela.cabecalhoNumerico}
          id="CreditosCliente_Rodape_TotalDisponivel"
        >{dadosCredito?.TotalDisponivel && dadosCredito?.TotalDisponivel > 0 ?
          globalMU.formatarValorNumerico(dadosCredito?.TotalDisponivel, 2, true) : null}
        </th>
        {permitirExibirCamposFormaPagamento
          ? <th colSpan={3} id="CreditosCliente_Rodape_ColunasFormaPagamento"></th>
          : <th id="CreditosCliente_Rodape_ColunaVazia"></th>
        }
        {usaFonteDeReceita && (
          <th id="CreditosCliente_Rodape_FonteDeReceita"></th>
        )}
      </tr>
    </tfoot>
  ), [dadosCredito, permitirExibirCamposFormaPagamento, usaFonteDeReceita]);

  const tabelaCreditos = React.useMemo(() => (
    <div className={estiloTabela.containerRolavel}
      id="CreditosCliente_ContainerTabelaCreditos"
    >
      <table className={`_table _w-full _border-collapse text-right ${estiloTabela.tabela}`}
        id="CreditosCliente_TabelaCreditos"
      >
        {cabecalhoTabelaCreditos}
        {corpoTabelaCreditos}
        {rodapeTabelaCreditos}
      </table>
    </div>
  ), [cabecalhoTabelaCreditos, corpoTabelaCreditos, rodapeTabelaCreditos]);

  return (
    <section id="CreditosCliente">
      {dadosCliente}
      {botaoImprmir}
      {tabelaCreditos}
      {<section id="CreditosCliente_EspacamentoFinal" style={{ marginBottom: "100px" }} />}
    </section>
  );
});

CreditosCliente.displayName = "CreditosCliente";
